import { type RoomOverviewResponse } from '../api/get-room-overview';

import { applyBlockLayoutToLanes } from './block-layout';
import { packEventsIntoLanes } from './lane-packing';
import {
  hasValidTimelineDuration,
  parseTimeToMinutes,
  TIMELINE_WIDTH_PX,
} from './timeline';
import {
  type OverviewEvent,
  type RoomRowData,
  UNASSIGNED_ROOM_ID,
} from './types';

function apiClassesToEvents(
  classes: RoomOverviewResponse['rooms'][number]['classes'],
  roomId: string,
): Array<OverviewEvent> {
  return classes.flatMap((classItem) => {
    const startMinutes = parseTimeToMinutes(classItem.startTime);
    const endMinutes = parseTimeToMinutes(classItem.endTime);

    if (!hasValidTimelineDuration(startMinutes, endMinutes)) {
      return [];
    }

    return [
      {
        classId: classItem.classId,
        className: classItem.className,
        instructorName: classItem.instructorName,
        startMinutes,
        endMinutes,
        locationName: classItem.locationName,
        roomId,
        color: classItem.color,
        type: classItem.type,
        bookingId: classItem.bookingId,
        studentName: classItem.studentName,
      },
    ];
  });
}

function buildRoomRow(
  roomId: string,
  roomName: string,
  locationName: string | undefined,
  events: Array<OverviewEvent>,
): RoomRowData {
  return {
    roomId,
    roomName,
    locationName,
    classCount: events.length,
    lanes: applyBlockLayoutToLanes(
      packEventsIntoLanes(events),
      TIMELINE_WIDTH_PX,
    ),
  };
}

export function buildRoomOverviewRowsFromApi(
  response: RoomOverviewResponse,
): Array<RoomRowData> {
  const rows = response.rooms.map((room) =>
    buildRoomRow(
      room.roomId,
      room.roomName,
      room.locationName,
      apiClassesToEvents(room.classes, room.roomId),
    ),
  );

  if (response.unassigned?.classes.length) {
    rows.push(
      buildRoomRow(
        UNASSIGNED_ROOM_ID,
        'Unassigned',
        response.unassigned.locationName,
        apiClassesToEvents(response.unassigned.classes, UNASSIGNED_ROOM_ID),
      ),
    );
  }

  return rows;
}
